import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { HTMLAttributes } from 'react';
import type { TSchema } from 'types/items';
import type { Configuration } from '../../Schema/schema.types';
import { getPluralLabel } from '../utils/pluralize';

interface Props extends HTMLAttributes<HTMLDivElement> {
  configuration: Configuration;
  handleExport: () => void;
  handleRefresh: () => void;
  rowsSelected: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: TSchema<any>;
  setShowConfirmDelete: (show: boolean) => void;
  setShowImportDialog: (show: boolean) => void;
  setShowPayrollExportDialog: (show: boolean) => void;
  setShowNewDialog: (show: boolean) => void;
  setShowUpdateDialog: (show: boolean) => void;
}

export default function StandardMenu({
  configuration,
  handleExport,
  handleRefresh,
  rowsSelected,
  schema,
  setShowConfirmDelete,
  setShowImportDialog,
  setShowPayrollExportDialog,
  setShowNewDialog,
  setShowUpdateDialog,
}: Props) {
  const pluralItemLabel = getPluralLabel(configuration.itemLabel);

  const showAddMenuItem = useMemo(
    () => schema?.permissions?.create && !configuration.addDisabled,
    [schema, configuration]
  );
  const showDeleteMenuItem = useMemo(
    () =>
      schema?.permissions?.delete &&
      !configuration.deleteDisabled &&
      !configuration.checkboxSelectionDisabled,
    [schema, configuration]
  );
  const showBulkUpdateMenuItem = useMemo(
    () =>
      schema?.permissions?.update &&
      !configuration.bulkUpdateDisabled &&
      !configuration.checkboxSelectionDisabled,
    [schema, configuration]
  );

  const showExportMenuItem = useMemo(
    () => !configuration.exportDisabled && !configuration.checkboxSelectionDisabled,
    [configuration]
  );

  const showImportMenuItem = useMemo(
    () =>
      schema?.permissions?.create &&
      !configuration.addDisabled &&
      !configuration.importDisabled,
    [schema, configuration]
  );

  const showPayrollExportMenuItem = useMemo(
    () => Boolean(configuration.customDialogs?.payrollExport),
    [configuration]
  );

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
      <Tooltip title="Refresh">
        <IconButton
          size="large"
          edge="end"
          color="inherit"
          aria-label="refresh-button"
          sx={{ mr: 2 }}
          onClick={handleRefresh}
        >
          <Icon>refresh</Icon>
        </IconButton>
      </Tooltip>

      {showAddMenuItem && (
        <Tooltip title={`New ${configuration.itemLabel}`}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="new-record-button"
            sx={{ mr: 2 }}
            onClick={() => setShowNewDialog(true)}
          >
            <Icon>add</Icon>
          </IconButton>
        </Tooltip>
      )}

      {showDeleteMenuItem && (
        <Tooltip title={`Delete Selected ${pluralItemLabel}`}>
          <span>
            <IconButton
              disabled={!rowsSelected}
              size="large"
              edge="start"
              color="inherit"
              aria-label="delete-button"
              sx={{ mr: 2 }}
              onClick={() => setShowConfirmDelete(true)}
            >
              <Icon>delete</Icon>
            </IconButton>
          </span>
        </Tooltip>
      )}

      {showBulkUpdateMenuItem && (
        <Tooltip title={`Update Selected ${pluralItemLabel}`}>
          <span>
            <IconButton
              disabled={!rowsSelected}
              size="large"
              edge="start"
              color="inherit"
              aria-label="mass-update-button"
              sx={{ mr: 2 }}
              onClick={() => setShowUpdateDialog(true)}
            >
              <Icon>edit_note</Icon>
            </IconButton>
          </span>
        </Tooltip>
      )}

      {showExportMenuItem && (
        <Tooltip title={`Export ${pluralItemLabel}`}>
          <span>
            <IconButton
              disabled={!rowsSelected}
              size="large"
              edge="start"
              color="inherit"
              aria-label="export-button"
              sx={{ mr: 2 }}
              onClick={() => handleExport()}
            >
              <Icon>download_file</Icon>
            </IconButton>
          </span>
        </Tooltip>
      )}

      {showImportMenuItem && (
        <Tooltip title={`Import ${pluralItemLabel}`}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="upload-button"
            sx={{ mr: 2 }}
            onClick={() => setShowImportDialog(true)}
          >
            <Icon>upload_file</Icon>
          </IconButton>
        </Tooltip>
      )}

      {showPayrollExportMenuItem && (
        <Tooltip title="Payroll Export">
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="payroll-export-button"
            sx={{ mr: 2 }}
            onClick={() => setShowPayrollExportDialog(true)}
          >
            <Icon>payments</Icon>
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
